import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from "recharts";
import { Shield, LogOut, FileText, Users, Download, RefreshCw, Clock, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AdminSubNav from "../components/AdminSubNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PERIODS = [
  { value: "1d", label: "Today" },
  { value: "7d", label: "7 Days" },
  { value: "1m", label: "1 Month" },
  { value: "3m", label: "3 Months" },
  { value: "12m", label: "12 Months" },
];

const COLORS = ["#f97316", "#3b82f6", "#10b981", "#8b5cf6", "#ef4444", "#14b8a6", "#f59e0b", "#6366f1"];

const AdminAuditPage = ({ user, setUser }) => {
  const [period, setPeriod] = useState("7d");
  const [specificDate, setSpecificDate] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [adminFilter, setAdminFilter] = useState("all");
  const [backfillStatus, setBackfillStatus] = useState(null);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [platformStats, setPlatformStats] = useState(null);
  const [statsRefreshedAt, setStatsRefreshedAt] = useState(null);
  const [refreshingStats, setRefreshingStats] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAudit();
    fetchPlatformStats();
  }, [period, specificDate]);

  // Auto-refresh stats: poll every 60s + refresh when the tab regains focus
  // (so navigating back from an item download shows the new count).
  useEffect(() => {
    const interval = setInterval(() => fetchPlatformStats(), 60_000);
    const onFocus = () => fetchPlatformStats();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const fetchPlatformStats = async () => {
    setRefreshingStats(true);
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlatformStats(res.data);
      setStatsRefreshedAt(new Date());
    } catch (err) {
      console.error("Platform stats fetch error:", err);
    } finally {
      setRefreshingStats(false);
    }
  };

  const fetchAudit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const tzOffset = new Date().getTimezoneOffset();
      let url = `${API}/admin/audit?period=${period}&tz_offset=${tzOffset}`;
      if (specificDate) {
        url = `${API}/admin/audit?period=1d&date=${specificDate}&tz_offset=${tzOffset}`;
      }
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      console.error("Audit fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const selectPeriod = (val) => {
    setSpecificDate("");
    setPeriod(val);
  };

  const handleDatePick = (e) => {
    setSpecificDate(e.target.value);
    setPeriod("1d"); // reset period chip visually
  };

  const activePeriodLabel = specificDate
    ? new Date(specificDate + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", year: "numeric", month: "short", day: "numeric" })
    : PERIODS.find((p) => p.value === period)?.label || period;

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    setUser(null);
    navigate("/");
  };

  // Filter admins by selection (client-side narrowing of the already-fetched data)
  const filteredAdmins = data?.admins?.filter(
    (a) => adminFilter === "all" || a.id === adminFilter
  ) || [];
  const filteredActivity = data?.today_activity?.filter((act) => {
    if (adminFilter === "all") return true;
    const target = data?.admins?.find((a) => a.id === adminFilter);
    if (!target) return false;
    // act.actor stores the admin name/email — match either
    return (
      act.actor === target.email ||
      act.actor === target.name ||
      act.actor?.toLowerCase() === target.email?.toLowerCase()
    );
  }) || [];

  const chartData = filteredAdmins.map((a) => ({
    name: a.name || a.email.split("@")[0],
    files: a.files_uploaded,
    users: a.users_managed,
  }));

  const totalFiles = filteredAdmins.reduce((s, a) => s + a.files_uploaded, 0);
  const totalUserActions = filteredAdmins.reduce((s, a) => s + a.users_managed, 0);
  const activeAdmins = adminFilter !== "all"
    ? (filteredAdmins[0] && (filteredAdmins[0].files_uploaded > 0 || filteredAdmins[0].users_managed > 0) ? 1 : 0)
    : (data?.active_admins ?? (data?.admins?.filter((a) => a.files_uploaded > 0 || a.users_managed > 0).length || 0));
  const totalItemsInPeriod = adminFilter !== "all" ? totalFiles : (data?.total_items_in_period || 0);
  const totalAllItems = data?.total_all_items || 0;
  const unattributed = data?.unattributed_items || 0;

  const exportCSV = () => {
    if (!data?.admins) return;
    const rows = ["Name,Email,Organization,Files Uploaded,Users Managed"];
    data.admins.forEach((a) => {
      rows.push(`"${a.name}","${a.email}","${a.organization}",${a.files_uploaded},${a.users_managed}`);
    });
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `admin-audit-${specificDate || period}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const runBackfill = async () => {
    if (!window.confirm(`This will attribute ${unattributed} unallocated items to you (${user?.email}). Items with audit logs will be attributed to the original uploader. Continue?`)) return;
    setBackfillLoading(true);
    setBackfillStatus(null);
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.post(`${API}/admin/audit/backfill`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000,
      });
      setBackfillStatus({ success: true, message: res.data.message });
      fetchAudit();
    } catch (err) {
      setBackfillStatus({ success: false, message: err.response?.data?.detail || "Backfill failed — try again" });
    } finally {
      setBackfillLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Admin Navigation */}
      <nav className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-xl font-semibold" style={{ fontFamily: "Fraunces, serif" }}>Admin Dashboard</h1>
            <div className="flex items-center space-x-4">
              <Button onClick={() => navigate("/")} variant="ghost" className="text-white hover:text-white hover:bg-slate-800">Public Site</Button>
              <Button onClick={handleLogout} variant="ghost" size="sm" className="text-white hover:text-white hover:bg-slate-800">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <AdminSubNav user={user} activeTab="audit" />

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-orange-500" />
            <h2 className="text-xl font-semibold" style={{ fontFamily: "Fraunces, serif" }} data-testid="audit-page-title">
              Admin Audit
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={runBackfill}
              disabled={backfillLoading || unattributed === 0}
              className={unattributed > 0 ? "border-amber-300 text-amber-700 hover:bg-amber-50" : ""}
              data-testid="audit-backfill-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${backfillLoading ? "animate-spin" : ""}`} />
              {backfillLoading ? "Running..." : unattributed > 0 ? `Fix ${unattributed} Unattributed` : "All Allocated"}
            </Button>
            {backfillStatus && (
              <span className={`text-xs px-2 py-1 rounded ${backfillStatus.success ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                {backfillStatus.message}
              </span>
            )}
            <Button variant="outline" size="sm" onClick={exportCSV} data-testid="audit-export-csv">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Period Selector */}
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="audit-period-selector">
          {PERIODS.map((p) => (
            <Button
              key={p.value}
              variant={!specificDate && period === p.value ? "default" : "outline"}
              size="sm"
              onClick={() => selectPeriod(p.value)}
              className={!specificDate && period === p.value ? "bg-orange-500 hover:bg-orange-600 text-white" : ""}
              data-testid={`audit-period-${p.value}`}
            >
              {p.label}
            </Button>
          ))}
          <div className="flex items-center gap-1.5 ml-2">
            <CalendarDays className="w-4 h-4 text-slate-500" />
            <Input
              type="date"
              value={specificDate}
              onChange={handleDatePick}
              className={`w-40 h-8 text-sm ${specificDate ? "border-orange-400 ring-1 ring-orange-200" : ""}`}
              max={new Date().toISOString().split("T")[0]}
              data-testid="audit-date-picker"
            />
          </div>
          <div className="flex items-center gap-1.5 ml-2">
            <Users className="w-4 h-4 text-slate-500" />
            <select
              value={adminFilter}
              onChange={(e) => setAdminFilter(e.target.value)}
              className={`h-8 text-sm border rounded-md px-2 bg-white ${adminFilter !== "all" ? "border-orange-400 ring-1 ring-orange-200 text-orange-700 font-medium" : "border-slate-300 text-slate-700"}`}
              data-testid="audit-admin-filter"
            >
              <option value="all">All admins</option>
              {data?.admins?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name || a.email}
                </option>
              ))}
            </select>
            {adminFilter !== "all" && (
              <button
                onClick={() => setAdminFilter("all")}
                className="text-xs text-orange-600 hover:text-orange-800 underline"
                data-testid="audit-admin-filter-clear"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading audit data...</div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white border border-slate-200 p-5 rounded-md" data-testid="audit-total-items">
                <FileText className="w-7 h-7 text-slate-400 mb-2" />
                <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Fraunces, serif" }}>{totalAllItems}</div>
                <div className="text-sm text-slate-600">Total Items in Archive</div>
                <div className="text-xs text-slate-500 mt-1">{totalItemsInPeriod} in this period{unattributed > 0 && <span className="text-amber-600"> ({unattributed} unattributed)</span>}</div>
              </div>
              <div className="bg-white border border-slate-200 p-5 rounded-md" data-testid="audit-total-files">
                <FileText className="w-7 h-7 text-orange-500 mb-2" />
                <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Fraunces, serif" }}>{totalFiles}</div>
                <div className="text-sm text-slate-600">Admin Uploads</div>
              </div>
              <div className="bg-white border border-slate-200 p-5 rounded-md" data-testid="audit-total-users-managed">
                <Users className="w-7 h-7 text-blue-500 mb-2" />
                <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Fraunces, serif" }}>{totalUserActions}</div>
                <div className="text-sm text-slate-600">User Management Actions</div>
              </div>
              <div className="bg-white border border-slate-200 p-5 rounded-md" data-testid="audit-active-admins">
                <Shield className="w-7 h-7 text-green-500 mb-2" />
                <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Fraunces, serif" }}>{activeAdmins}</div>
                <div className="text-sm text-slate-600">Active Admins</div>
              </div>
            </div>

            {/* Platform Stats */}
            {platformStats && (
              <>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-slate-700">Platform statistics</h3>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    {statsRefreshedAt && (
                      <span data-testid="stats-refreshed-at">
                        Updated {statsRefreshedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={fetchPlatformStats}
                      disabled={refreshingStats}
                      className="p-1 rounded hover:bg-slate-100 transition-colors disabled:opacity-50"
                      title="Refresh stats now"
                      data-testid="refresh-platform-stats-btn"
                    >
                      <RefreshCw className={`w-4 h-4 ${refreshingStats ? "animate-spin" : ""}`} />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  <div className="bg-white border border-slate-200 p-5 rounded-md">
                    <Users className="w-7 h-7 text-orange-500 mb-2" />
                    <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Fraunces, serif" }}>{platformStats.total_users || 0}</div>
                    <div className="text-sm text-slate-600">Total Users</div>
                  </div>
                  <div className="bg-white border border-slate-200 p-5 rounded-md">
                    <Users className="w-7 h-7 text-green-500 mb-2" />
                    <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Fraunces, serif" }}>{platformStats.total_active_users || 0}</div>
                    <div className="text-sm text-slate-600">Active Users (trial + subscribed)</div>
                  </div>
                  <div className="bg-white border border-slate-200 p-5 rounded-md">
                    <Download className="w-7 h-7 text-orange-500 mb-2" />
                    <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Fraunces, serif" }} data-testid="audit-total-downloads">{platformStats.total_downloads || 0}</div>
                    <div className="text-sm text-slate-600">Total Downloads</div>
                  </div>
                  <div className="bg-white border border-slate-200 p-5 rounded-md">
                    <FileText className="w-7 h-7 text-blue-500 mb-2" />
                    <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Fraunces, serif" }}>{platformStats.system_status || "Active"}</div>
                    <div className="text-sm text-slate-600">System Status</div>
                  </div>
                </div>
              </>
            )}

            {/* Activity Feed */}
            {data?.today_activity?.length > 0 && filteredActivity.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-md mb-8" data-testid="today-activity-feed">
                <div className="p-4 border-b border-slate-200 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-orange-500" />
                  <h3 className="text-lg font-semibold" style={{ fontFamily: "Fraunces, serif" }}>
                    {activePeriodLabel} Activity
                    {adminFilter !== "all" && (
                      <span className="ml-2 text-sm font-normal text-slate-500">
                        — {data?.admins?.find((a) => a.id === adminFilter)?.email}
                      </span>
                    )}
                  </h3>
                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">
                    {filteredActivity.length}
                  </span>
                </div>
                <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
                  {filteredActivity.map((act, idx) => (
                    <div key={idx} className="px-4 py-2.5 flex items-center gap-3 text-sm">
                      <span className="text-slate-400 text-xs w-16 shrink-0">
                        {act.time ? new Date(act.time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                      </span>
                      <span className="font-medium text-slate-800">{act.actor}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        act.action.includes("create") ? "bg-green-100 text-green-700" :
                        act.action.includes("delete") ? "bg-red-100 text-red-700" :
                        act.action.includes("update") || act.action.includes("edit") ? "bg-blue-100 text-blue-700" :
                        "bg-slate-100 text-slate-600"
                      }`}>
                        {act.action.replace(/_/g, " ")}
                      </span>
                      <span className="text-slate-500">{act.entity}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bar Chart */}
            {chartData.length > 0 && (
              <div className="bg-white border border-slate-200 p-6 rounded-md mb-8" data-testid="audit-chart">
                <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: "Fraunces, serif" }}>
                  {adminFilter !== "all"
                    ? `Activity for ${data?.admins?.find((a) => a.id === adminFilter)?.email || "Admin"} — ${activePeriodLabel}`
                    : `Admin Activity — ${activePeriodLabel}`}
                </h3>
                <ResponsiveContainer width="100%" height={Math.max(300, chartData.length * 50)}>
                  <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 100, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" tick={{ fontSize: 12, fill: "#64748b" }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 12, fill: "#334155" }} width={90} />
                    <Tooltip
                      contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "13px" }}
                    />
                    <Legend />
                    <Bar dataKey="files" name="Files Uploaded" fill="#f97316" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="users" name="Users Managed" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Admin Table */}
            <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="audit-table">
              <div className="p-4 border-b border-slate-200">
                <h3 className="text-lg font-semibold" style={{ fontFamily: "Fraunces, serif" }}>Detailed Admin Activity</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-slate-600 font-medium">Name</th>
                      <th className="px-4 py-3 text-left text-slate-600 font-medium">Email</th>
                      <th className="px-4 py-3 text-left text-slate-600 font-medium">Organization</th>
                      <th className="px-4 py-3 text-right text-slate-600 font-medium">Files Uploaded</th>
                      <th className="px-4 py-3 text-right text-slate-600 font-medium">Users Managed</th>
                      <th className="px-4 py-3 text-center text-slate-600 font-medium">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAdmins.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                          {adminFilter !== "all" ? "No activity for this admin in this period." : "No admin activity in this period."}
                        </td>
                      </tr>
                    )}
                    {filteredAdmins.map((admin) => (
                      <tr
                        key={admin.id}
                        className={`border-t border-slate-100 hover:bg-slate-50 transition ${selectedAdmin === admin.id ? "bg-orange-50" : ""}`}
                        data-testid={`audit-row-${admin.id}`}
                      >
                        <td className="px-4 py-3 text-slate-900 font-medium">{admin.name || "—"}</td>
                        <td className="px-4 py-3 text-slate-700">{admin.email}</td>
                        <td className="px-4 py-3 text-slate-700">{admin.organization || "Public"}</td>
                        <td className="px-4 py-3 text-right font-mono text-slate-800">{admin.files_uploaded}</td>
                        <td className="px-4 py-3 text-right font-mono text-slate-800">{admin.users_managed}</td>
                        <td className="px-4 py-3 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedAdmin(selectedAdmin === admin.id ? null : admin.id)}
                            data-testid={`audit-details-btn-${admin.id}`}
                          >
                            {selectedAdmin === admin.id ? "Hide" : "View"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {/* Expandable category detail */}
                    {filteredAdmins.map((admin) =>
                      selectedAdmin === admin.id && Object.keys(admin.categories || {}).length > 0 ? (
                        <tr key={`${admin.id}-detail`} className="bg-orange-50/50">
                          <td colSpan={6} className="px-6 py-3">
                            <div className="text-xs font-medium text-slate-500 mb-2 uppercase tracking-wide">Upload Breakdown by Category</div>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(admin.categories).map(([cat, count]) => (
                                <span key={cat} className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-white border border-slate-200 text-slate-700">
                                  {cat}: <span className="font-bold text-orange-600">{count}</span>
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ) : null
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminAuditPage;
